/**
 * Per-site dashboard schemas.
 *
 * The /sites/:slug page composes 6 distinct shapes — site summary, device
 * roll-ups by class, WAN uplinks, throughput series, topology tree, and
 * the two paginated tables (connected clients + devices). They're loaded
 * in parallel by useSiteDashboard.load().
 */
import { z } from "zod";

/** Normalized device status — single set of values across vendors. */
const SiteDeviceStatusSchema = z.enum([
  "online",
  "alerting",
  "offline",
  "dormant",
]);
export type SiteDeviceStatus = z.infer<typeof SiteDeviceStatusSchema>;

/** Aggregated count of devices in a class (appliance/switch/wireless). */
export const DeviceClassRollupSchema = z.object({
  /** Display label, e.g. "Switches". */
  label: z.string(),
  /** Total devices of this class on the site. */
  total: z.number(),
  /** Subtotals keyed by status — at minimum has online/dormant entries. */
  byStatus: z.record(SiteDeviceStatusSchema, z.number()),
});
export type DeviceClassRollup = z.infer<typeof DeviceClassRollupSchema>;

/** A single WAN uplink — IPs, DNS, status. */
export const SiteWanUplinkSchema = z.object({
  /** "WAN1" / "WAN2". */
  label: z.string(),
  /** Hardware model carrying this uplink, e.g. "MX450". */
  model: z.string(),
  /** "active" — currently carrying traffic. "ready" — failover stand-by. */
  state: z.enum(["active", "ready", "down"]),
  publicIp: z.string().nullable(),
  lanIp: z.string().nullable(),
  gateway: z.string().nullable(),
  primaryDns: z.string().nullable(),
  secondaryDns: z.string().nullable(),
});
export type SiteWanUplink = z.infer<typeof SiteWanUplinkSchema>;

/** One bin on the throughput chart — bytes per second per direction. */
const ThroughputPointSchema = z.object({
  /** ISO timestamp. */
  t: z.string(),
  /** Received bits/sec at this bin. */
  rxBps: z.number(),
  /** Transmitted bits/sec at this bin. */
  txBps: z.number(),
});
export type ThroughputPoint = z.infer<typeof ThroughputPointSchema>;
export const ThroughputSeriesSchema = z.array(ThroughputPointSchema);

/** Connected client row — for the live clients table. */
const ConnectedClientSchema = z.object({
  id: z.string(),
  /** Best-effort hostname or UUID fallback. */
  name: z.string(),
  mac: z.string(),
  manufacturer: z.string().nullable().optional(),
  os: z.string().nullable().optional(),
  ip: z.string().nullable(),
  /** SSID name when wireless; "Wired" when on a switch port. */
  ssid: z.string().nullable(),
  /** VLAN id as a string ("520") or "Port 32" for wired clients. */
  vlanOrPort: z.string().nullable(),
  /** ISO timestamp last seen. */
  lastSeen: z.string(),
  /** Status — usually online or offline. */
  online: z.boolean(),
  /** Bytes received this hour. */
  bytesDown: z.number(),
  /** Bytes transmitted this hour. */
  bytesUp: z.number(),
});
export type ConnectedClient = z.infer<typeof ConnectedClientSchema>;
export const ConnectedClientListSchema = z.array(ConnectedClientSchema);

/** Device row for the per-site Devices table. */
const SiteDeviceSchema = z.object({
  serial: z.string(),
  name: z.string(),
  model: z.string(),
  macAddress: z.string().nullable().optional(),
  address: z.string().nullable().optional(),
  tags: z.array(z.string()).optional().default([]),
  notes: z.string().nullable().optional(),
  status: SiteDeviceStatusSchema,
  lanIp: z.string().nullable(),
  publicIp: z.string().nullable(),
  /** ISO timestamp last seen by the cloud. Null if never. */
  lastSeen: z.string().nullable(),
  /** Which class this device belongs to — drives the topology tier. */
  deviceClass: z.enum(["wan", "appliance", "switch", "wireless"]),
});
export type SiteDevice = z.infer<typeof SiteDeviceSchema>;
export const SiteDeviceListSchema = z.array(SiteDeviceSchema);

/** Organization firmware upgrade event scoped to a site network. */
const FirmwareUpgradeSchema = z.object({
  upgradeId: z.string(),
  upgradeBatchId: z.string(),
  networkId: z.string(),
  networkName: z.string(),
  status: z.string(),
  phase: z.enum(["upcoming", "completed"]),
  scheduledAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  productTypes: z.array(z.string()),
  fromVersion: z.string().nullable(),
  toVersion: z.string().nullable(),
  releaseType: z.string().nullable(),
  timezone: z.string(),
});
export type FirmwareUpgrade = z.infer<typeof FirmwareUpgradeSchema>;
export const FirmwareUpgradeListSchema = z.array(FirmwareUpgradeSchema);

/** Network alert history row for the dashboard alerts table. */
const AlertHistoryRowSchema = z.object({
  occurredAt: z.string().nullable(),
  alertTypeId: z.string().nullable(),
  alertType: z.string(),
  deviceSerial: z.string().nullable(),
  emailSentAt: z.string().nullable(),
  pushSentAt: z.string().nullable(),
  smsSentAt: z.string().nullable(),
  webhookSentAt: z.string().nullable(),
});
export type AlertHistoryRow = z.infer<typeof AlertHistoryRowSchema>;
export const AlertHistoryListSchema = z.array(AlertHistoryRowSchema);

/** Health assurance alert row (organization-level alert filtered to this network). */
const AssuranceAlertSchema = z.object({
  id: z.string(),
  category: z.string(),
  type: z.string(),
  title: z.string(),
  description: z.string().optional().default(""),
  severity: z.string(),
  startedAt: z.string().nullable(),
  resolvedAt: z.string().nullable(),
  dismissedAt: z.string().nullable(),
  networkId: z.string(),
  networkName: z.string(),
  deviceSerial: z.string().nullable(),
  deviceName: z.string().nullable(),
  deviceType: z.string().nullable(),
});
export type AssuranceAlert = z.infer<typeof AssuranceAlertSchema>;
export const AssuranceAlertListSchema = z.array(AssuranceAlertSchema);

/** Wireless onboarding/connection failure row from Meraki telemetry. */
const WirelessFailedConnectionSchema = z.object({
  ssidNumber: z.number().nullable(),
  vlan: z.number().nullable(),
  clientMac: z.string().nullable(),
  serial: z.string().nullable(),
  radio: z.number().nullable().optional(),
  failureStep: z.string(),
  type: z.string(),
  ts: z.string().nullable(),
});
export type WirelessFailedConnection = z.infer<typeof WirelessFailedConnectionSchema>;
export const WirelessFailedConnectionListSchema = z.array(WirelessFailedConnectionSchema);

const SiteLicenseRowSchema = z.object({
  id: z.string(),
  licenseType: z.string().nullable().optional(),
  licenseKey: z.string().nullable().optional(),
  deviceSerial: z.string().nullable().optional(),
  networkId: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  expirationDate: z.string().nullable().optional(),
  seatCount: z.number().nullable().optional(),
});
export type SiteLicenseRow = z.infer<typeof SiteLicenseRowSchema>;
export const SiteLicenseListSchema = z.array(SiteLicenseRowSchema);

/** Top-level site summary returned by GET /api/sites/:slug. */
export const SiteSummarySchema = z.object({
  /** Short slug used in URLs (e.g. "wasci"). Last 5 chars of the dashboard code. */
  slug: z.string(),
  /** Primary site identity token (Meraki network id when mapped, otherwise property code/slug). */
  networkId: z.string(),
  /** Full Meraki dashboard code, e.g. "NA-USA-DC-GIA-WASCI". */
  code: z.string(),
  /** Synapse inn code (5-char hotel code). */
  innCode: z.string().nullable().optional(),
  /** Synapse navision code, when available. */
  navisionCode: z.string().nullable().optional(),
  /** Synapse full property address, when available. */
  address: z.string().nullable().optional(),
  /** True when the Synapse property name marks it as lab/test/non-production. */
  isTest: z.boolean().optional().default(false),
  /** Synapse brand name when available. */
  brandName: z.string().nullable().optional(),
  /** Synapse umbrella brand when available. */
  umbrellaBrand: z.string().nullable().optional(),
  /** Synapse total room count when available. */
  totalRooms: z.number().nullable().optional(),
  /** Synapse timezone name (for example America/Chicago). */
  timezoneName: z.string().nullable().optional(),
  /** Synapse timezone offset in minutes from GMT. */
  timezoneOffsetMinutes: z.number().nullable().optional(),
  /** Synapse RX/down bandwidth cap in Kbps. */
  rxLimitKbps: z.number().optional().default(0),
  /** Synapse TX/up bandwidth cap in Kbps. */
  txLimitKbps: z.number().optional().default(0),
  /** Friendly Opus-side name. */
  friendlyName: z.string(),
  /** Org / brand the site belongs to (display-only). */
  org: z.string(),
  /** Vendor source for the network. */
  vendor: z.string().optional(),
  /** True when Synapse metadata could be fetched for this response. */
  synapseAvailable: z.boolean().optional(),
  /** Non-fatal warning when Synapse is unavailable and vendor-only data is shown. */
  synapseWarning: z.string().nullable().optional(),
  /** Optional deep link to the vendor's dashboard for this site/network. */
  externalDashboardUrl: z.string().url().nullable().optional(),
  /** Active clients in last 5 min. */
  activeClients: z.number(),
  /** Heavy users in last hour (clients consuming >1 GB). */
  heavyClientsLastHour: z.number(),
  /** Peak total throughput in the selected range (bps). */
  peakBps: z.number(),
  /** Total uplinks + currently active subset. */
  uplinks: z.object({ total: z.number(), active: z.number() }),
});
export type SiteSummary = z.infer<typeof SiteSummarySchema>;

/** All data sections for a per-site dashboard page, loaded in parallel. */
export type SiteDashboard = {
  summary: SiteSummary;
  rollups: DeviceClassRollup[];
  wanUplinks: SiteWanUplink[];
  throughput: ThroughputPoint[];
  /** Client aggregate bandwidth over the same window as throughput. */
  clientBandwidthUsage: ThroughputPoint[];
  devices: SiteDevice[];
  clients: ConnectedClient[];
  firmwareUpgrades: FirmwareUpgrade[];
  alertsHistory: AlertHistoryRow[];
  licenses: SiteLicenseRow[];
  assuranceAlerts: AssuranceAlert[];
  wirelessFailedConnections: WirelessFailedConnection[];
};
